import type { UnifiedTableRecord } from "../../../shared/ui/data-table/model/data-table-contract";

export interface ResultDetailRow {
  label: string;
  value: string;
}

export interface ResultRecord {
  id: string;
  resultType: "Lab" | "Procedure";
  resultDate: string;
  resultName: string;
  completionStatus: string;
  reviewedStatus: string;
  source: string;
  lastUpdated: string;
  orderDetails: ResultDetailRow[];
  resultDetails: ResultDetailRow[];
  resultNotes?: string;
}

const makeOrderDetails = (
  name: string,
  completionStatus: string,
  reviewedStatus: string,
  orderDate: string,
  provider: string,
  diagnosis: string,
  source: string,
  orderId: string,
): ResultDetailRow[] => [
  { label: "Name", value: name },
  { label: "Completion Status", value: completionStatus },
  { label: "Reviewed Status", value: reviewedStatus },
  { label: "Order Date", value: orderDate },
  { label: "Provider", value: provider },
  { label: "Ordered For", value: "-" },
  { label: "Performed On", value: orderDate },
  { label: "Report Date", value: orderDate },
  {
    label: "Reviewed Date",
    value: reviewedStatus === "Reviewed" ? orderDate : "-",
  },
  { label: "Reviewed By", value: provider },
  { label: "Notify Provider", value: provider },
  { label: "LOINC", value: "-" },
  { label: "Comment", value: "-" },
  { label: "Diagnosis", value: diagnosis },
  { label: "Source", value: source },
  { label: "Order ID", value: orderId },
  { label: "Ext Accession", value: "-" },
  { label: "Req Code", value: orderId.slice(-5) },
  { label: "Text Description", value: "-" },
];

const makeResultDetails = (
  name: string,
  value: string,
  completionStatus: string,
  reviewedStatus: string,
): ResultDetailRow[] => [
  { label: "Name", value: name },
  { label: "Value", value },
  { label: "Flags", value: "-" },
  { label: "Units", value: "-" },
  { label: "Normal Range", value: "-" },
  { label: "Completion Status", value: completionStatus },
  { label: "Reviewed Status", value: reviewedStatus },
  { label: "Change Reason", value: "-" },
];

export const results: ResultRecord[] = [
  {
    id: "urine-culture-routine-87086",
    resultType: "Lab",
    resultDate: "04/06/2022",
    resultName: "URINE CULTURE ROUTINE (87086)",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "QUEST",
    lastUpdated: "04/06/2022 07:47 PM",
    orderDetails: makeOrderDetails(
      "URINE CULTURE ROUTINE (87086)",
      "Final",
      "Reviewed",
      "04/04/2022",
      "Barton, Elizabeth",
      "Vaginitis",
      "QUEST",
      "000017628",
    ),
    resultDetails: makeResultDetails(
      "CULTURE, URINE, ROUTINE",
      "SEE NOTE",
      "Final",
      "Reviewed",
    ),
    resultNotes:
      "CULTURE, URINE, ROUTINE Micro Number: 13783542 Test Status: Final Specimen Source: Urine, vaginal Specimen Quality: Adequate Result: No Growth Test Performed at: QUEST DIAGNOSTICS-MIAMI 10200 COMMERCE PARKWAY MIRAMAR, FL 33025-3938 JULIE L FRIEDMAN, MD",
  },
  {
    id: "vaginitis-dna-probe-87510",
    resultType: "Lab",
    resultDate: "04/05/2022",
    resultName: "Vaginitis, DNA Probe, Affirm VP III (87510)",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "QUEST",
    lastUpdated: "04/05/2022 11:20 AM",
    orderDetails: makeOrderDetails(
      "Vaginitis, DNA Probe, Affirm VP III (87510)",
      "Final",
      "Reviewed",
      "04/04/2022",
      "Barton, Elizabeth",
      "Vaginitis",
      "QUEST",
      "000017510",
    ),
    resultDetails: makeResultDetails(
      "AFFIRM VPIII MICROBIAL IDENTIFICATION",
      "SEE NOTE",
      "Final",
      "Reviewed",
    ),
    resultNotes:
      "Affirm VPIII microbial identification completed. Candida species, Gardnerella vaginalis, and Trichomonas vaginalis results are available in the source report.",
  },
  {
    id: "urinalysis-ua-dipstick-81003",
    resultType: "Lab",
    resultDate: "04/04/2022",
    resultName: "URINALYSIS (UA DIPSTICK) (URGENT CARE) (81003)",
    completionStatus: "Partial",
    reviewedStatus: "Partially Reviewed",
    source: "Office",
    lastUpdated: "04/04/2022 05:28 PM",
    orderDetails: makeOrderDetails(
      "URINALYSIS (UA DIPSTICK) (URGENT CARE) (81003)",
      "Partial",
      "Partially Reviewed",
      "04/04/2022",
      "Barton, Elizabeth",
      "Dysuria",
      "Office",
      "000018103",
    ),
    resultDetails: makeResultDetails(
      "URINALYSIS DIPSTICK",
      "SEE NOTE",
      "Partial",
      "Partially Reviewed",
    ),
    resultNotes:
      "Urinalysis was partially reviewed. See original source for individual dipstick components and review history.",
  },
  {
    id: "cbc-with-diff-85025",
    resultType: "Lab",
    resultDate: "08/22/2017",
    resultName: "CBC WITH DIFF WITH PLATELETS (85025)",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "QUEST",
    lastUpdated: "08/22/2017 04:18 PM",
    orderDetails: makeOrderDetails(
      "CBC WITH DIFF WITH PLATELETS (85025)",
      "Final",
      "Reviewed",
      "08/22/2017",
      "Goldstein, Aaron",
      "Follow-up examination",
      "QUEST",
      "000014725",
    ),
    resultDetails: makeResultDetails(
      "CBC WITH DIFFERENTIAL",
      "SEE NOTE",
      "Final",
      "Reviewed",
    ),
  },
  {
    id: "comprehensive-metabolic-panel-80053",
    resultType: "Lab",
    resultDate: "08/22/2017",
    resultName: "COMPREHENSIVE METABOLIC PANEL (80053)",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "QUEST",
    lastUpdated: "08/22/2017 04:18 PM",
    orderDetails: makeOrderDetails(
      "COMPREHENSIVE METABOLIC PANEL (80053)",
      "Final",
      "Reviewed",
      "08/22/2017",
      "Goldstein, Aaron",
      "Follow-up examination",
      "QUEST",
      "000014726",
    ),
    resultDetails: makeResultDetails(
      "COMPREHENSIVE METABOLIC PANEL",
      "SEE NOTE",
      "Final",
      "Reviewed",
    ),
  },
  {
    id: "lipase-83690",
    resultType: "Lab",
    resultDate: "08/22/2017",
    resultName: "LIPASE (83690)",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "QUEST",
    lastUpdated: "08/22/2017 04:16 PM",
    orderDetails: makeOrderDetails(
      "LIPASE (83690)",
      "Final",
      "Reviewed",
      "08/22/2017",
      "Goldstein, Aaron",
      "Follow-up examination",
      "QUEST",
      "000014727",
    ),
    resultDetails: makeResultDetails("LIPASE", "28", "Final", "Reviewed"),
  },
  {
    id: "unattended-electrical-stimulation-97014",
    resultType: "Procedure",
    resultDate: "12/05/2017",
    resultName: "UNATTENDED ELECTRICAL STIMULATION (97014)",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "BCHC Rehab Center",
    lastUpdated: "12/05/2017 09:32 AM",
    orderDetails: makeOrderDetails(
      "UNATTENDED ELECTRICAL STIMULATION (97014)",
      "Final",
      "Reviewed",
      "12/05/2017",
      "Testa, Nicholas",
      "Regional enteritis of ileum",
      "BCHC Rehab Center",
      "00000097014",
    ),
    resultDetails: makeResultDetails(
      "UNATTENDED ELECTRICAL STIMULATION",
      "Completed",
      "Final",
      "Reviewed",
    ),
    resultNotes:
      "Therapy completed as ordered. Patient tolerated treatment without complication.",
  },
  {
    id: "therapeutic-exercise-97110",
    resultType: "Procedure",
    resultDate: "12/05/2017",
    resultName: "THERAPEUTIC EXERCISE (97110)",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "BCHC Rehab Center",
    lastUpdated: "12/05/2017 09:34 AM",
    orderDetails: makeOrderDetails(
      "THERAPEUTIC EXERCISE (97110)",
      "Final",
      "Reviewed",
      "12/05/2017",
      "Testa, Nicholas",
      "Regional enteritis of ileum",
      "BCHC Rehab Center",
      "00000097110",
    ),
    resultDetails: makeResultDetails(
      "THERAPEUTIC EXERCISE",
      "Completed",
      "Final",
      "Reviewed",
    ),
  },
  {
    id: "patient-educated-exercise-technique",
    resultType: "Procedure",
    resultDate: "12/05/2017",
    resultName: "Rehab - Patient educated in correct exercise technique.",
    completionStatus: "Ordered",
    reviewedStatus: "Reviewed",
    source: "BCHC Rehab Center",
    lastUpdated: "12/05/2017 09:35 AM",
    orderDetails: makeOrderDetails(
      "Rehab - Patient educated in correct exercise technique.",
      "Ordered",
      "Reviewed",
      "12/05/2017",
      "Testa, Nicholas",
      "Regional enteritis of ileum",
      "BCHC Rehab Center",
      "00000097111",
    ),
    resultDetails: makeResultDetails(
      "PATIENT EDUCATION",
      "Documented",
      "Ordered",
      "Reviewed",
    ),
  },
  {
    id: "follow-up-if-symptoms-worsen",
    resultType: "Procedure",
    resultDate: "01/27/2017",
    resultName: "Follow up if no improvement or if symptoms worsen",
    completionStatus: "Final",
    reviewedStatus: "Reviewed",
    source: "Office",
    lastUpdated: "01/27/2017 02:48 PM",
    orderDetails: makeOrderDetails(
      "Follow up if no improvement or if symptoms worsen",
      "Final",
      "Reviewed",
      "01/27/2017",
      "Barton, Elizabeth",
      "Follow-up examination",
      "Office",
      "000000599",
    ),
    resultDetails: makeResultDetails(
      "FOLLOW UP",
      "Completed",
      "Final",
      "Reviewed",
    ),
  },
];

export const resultsRecord: UnifiedTableRecord = {
  module: "results",
  pageTitle: "Results",
  sections: [
    {
      id: "results",
      title: "Results",
      tables: [
        {
          id: "results-table",
          columns: [
            {
              key: "details",
              header: "",
              rendererId: "resultDetails",
              width: 48,
              frozen: "left",
            },
            { key: "resultType", header: "Type", sortable: true, width: 120 },
            { key: "resultDate", header: "Date", sortable: true, width: 140 },
            {
              key: "resultName",
              header: "Description",
              sortable: true,
              width: 380,
              rendererId: "wrappingCell",
            },
            {
              key: "completionStatus",
              header: "Completion Status",
              sortable: true,
              width: 170,
            },
            {
              key: "reviewedStatus",
              header: "Reviewed Status",
              sortable: true,
              width: 170,
            },
            { key: "source", header: "Source", sortable: true, width: 180 },
            {
              key: "lastUpdated",
              header: "Last Updated",
              sortable: true,
              width: 180,
            },
          ],
          rows: results.map((result) => ({
            id: result.id,
            details: result.id,
            resultType: result.resultType,
            resultDate: result.resultDate,
            resultName: result.resultName,
            completionStatus: result.completionStatus,
            reviewedStatus: result.reviewedStatus,
            source: result.source,
            lastUpdated: result.lastUpdated,
          })),
          ui: {
            searchable: true,
            searchPlaceholder: "Search...",
            searchKeys: [
              "resultType",
              "resultDate",
              "resultName",
              "completionStatus",
              "reviewedStatus",
              "source",
              "lastUpdated",
            ],
            downloadable: ["csv", "xls", "pdf"],
            showTableDownloads: true,
            defaultRowsPerPage: 10,
            rowsPerPageOptions: [5, 10, 20, 25],
            emptyMessage: "No results found.",
          },
        },
      ],
    },
  ],
};

export const getResultById = (resultId?: string): ResultRecord =>
  results.find((result) => result.id === resultId) ?? results[0];
